import React, { useState, useEffect } from 'react';
import moment from 'moment';

import Button from 'b2b-ui-components-lib/lib/components/button';
import {
  faOnDedicatedSummaryPageCancel,
  faOnDedicatedSummaryCancelOrderPopupLoad,
  faOnDedicatedSummaryCancelOrderPopupSubmit,
  faOnDedicatedSummaryPageSubmit
} from 'dedicated/services/analytics.service';
import { GENERIC_ERROR_MESSAGE, ORDER_TYPE, ROUTES } from 'common/constants/app.constants';
import { NOTIFICATION_MESSAGE } from 'home/view/manage-feasibility/constants/multiSite-constants';
import { isOrderConfirmationUrl } from 'dedicated/services/summary.service';
import CancelOrderPopup from 'dedicated/components/cancelOrderPopup/cancelOrderPopup';
import EmailPopup from 'dedicated/components/emailPopup/emailPopup';
import { getCurrentLead, getCurrentOrder, saveOrder } from 'tokenGeneration/lead.util';
import { fetchOrder as fetchOrderAsync, submitOrderForSummary, updateOrderForSummary } from 'common/services/http.service';
import { formatDate, isNull, isOrderSubmittedByCustomer, numberWithCommas, showServiceError, trackAnalytics } from 'common/utils/app.utils';
import WidgetComponent from './WidgetComponent';

const FooterComponent = (props) => {
  const { history } = props;
  let {
    setNotification,
    cpmAccordionMeta,
    cpmFormSchema,
    selectedOption,
    appointmentData,
    setAppointmentData,
    siteReadyByDate,
    setSiteReadyByDate,
    showWidget,
    setShowWidget,
    appointmentId,
    setSelectedAppointmentId,
    customerNeedByDate,
    setCustomerNeedByDate,
    setError,
    isOrderConfirmation,
    setIsOrderConfirmation,
    circleData
  } = props;

  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [viewMore, setViewMore] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showEmailPopup, setShowEmailPopup] = useState(false);

  const order = getCurrentOrder?.();
  const selectedMedia = order?.lead?.connectivityLineItems?.[0]?.selectedMedia?.[0]?.media;
  const isThirdPartyMedia = selectedMedia === 'Third Party' || selectedMedia === 'TP';
  const singleSiteCircle = order?.lead?.connectivityLineItems?.[0]?.address?.circle;
  const isCircleEligible = (circle) => circleData && Array.isArray(circleData.eligibleCircles) && (circleData.allCircles || circleData.eligibleCircles.includes(circle));

  if (!order) {
    return null;
  }

  const {
    lead: {
      connectivityLineItems: [connectivityLineItem],
      lineItemCount,
      customer
    },
    ocLinkSentAtList = [],
    orderStatus,
    billingLevel
  } = order;

  const { planDetail = {}, email } = connectivityLineItem;
  const ocLinkSentAt = ocLinkSentAtList?.join(', ').toString();

  const checkOrderConfirmationStatus = ({ orderStatus }) => {
    const status = (orderStatus && [ORDER_TYPE.ORDER_CONFIRMATION, ORDER_TYPE.ORDER_CONFIRMATION_PENDING]).includes(orderStatus) || isOrderConfirmationUrl();
    setIsOrderConfirmation(status);
  };

  const fetchOrder = (cb = () => {}) => {
    setShowLoader(true);
    fetchOrderAsync().then(
      ({ data }) => {
        if (data && data.success && data.result) {
          const order = data.result;
          saveOrder(order);
          redirectToThankYou({}, order);
          checkOrderConfirmationStatus(order);
          cb?.();
        }
        setShowLoader(false);
      },
      (err) => {
        setShowLoader(false);
        console.error(err);
        if (isOrderConfirmation) {
          history.push(ROUTES.B2B_FAILURE);
        }
      }
    );
  };

  const redirectToThankYou = (submitOrderRes = {}, order, isSubmitted = false) => {
    const {
      lead: {
        customer: { partyId, segment },
        lineItemCount = ''
      },
      orderId,
      orderStatus
    } = order;
    if (isOrderSubmittedByCustomer(orderStatus) || isSubmitted) {
      const dateStamp = (submitOrderRes && submitOrderRes.data && submitOrderRes.data.result && submitOrderRes.data.result.submittedAt) || '';
      let redirectUrl = ROUTES.DEDICATED_THANK_YOU;
      history.push(redirectUrl, {
        leadId: orderId,
        userType: segment,
        hideNetworkSteps: true,
        via: 'SUMMARY',
        isThankYouAuth: true,
        noOfCircuits: lineItemCount,
        dateStamp
      });
    } else if (orderStatus === ORDER_TYPE.ORDER_CANCELLED) {
      let redirectUrl = ROUTES.DEDICATED_CANCEL;
      if (partyId) {
        redirectUrl = redirectUrl + '?partyId=' + partyId;
      }
      history.push(redirectUrl, { leadId: orderId, userType: segment, hideNetworkSteps: true, via: 'SUMMARY', isThankYouAuth: true, noOfCircuits: lineItemCount });
    }
  };

  const submitOrder = () => {
    setShowLoader(true);
    submitOrderForSummary().then(
      (resp) => {
        setShowLoader(false);
        redirectToThankYou(resp, order, true);
      },
      (error) => {
        console.error(error);
        setShowLoader(false);
        const errorMsg = (error && error.response && error.response.data && error.response.data.errorMsg) || GENERIC_ERROR_MESSAGE;

        showServiceError({
          show: true,
          data: errorMsg,
          redirect: false
        });
      }
    );
  };

  const getUpdateOrderRequest = () => {
    if (selectedOption?.id === 'Yes') {
      return {
        customerNeedDate: formatDate(customerNeedByDate) || '',
        customerProjectManagerDetails: {
          name: cpmFormSchema?.name?.value,
          contactNumber: cpmFormSchema?.mobileNumber?.value,
          emailId: cpmFormSchema?.emailAddress?.value
        },
        siteReadiness: true,
        siteReadinessDate: '',
        siteInstallAppointmentDetail: {
          appointmentId
        }
      };
    } else {
      return {
        customerNeedDate: formatDate(customerNeedByDate) || '',
        customerProjectManagerDetails: {
          name: cpmFormSchema?.name?.value,
          contactNumber: cpmFormSchema?.mobileNumber?.value,
          emailId: cpmFormSchema?.emailAddress?.value
        },
        siteReadiness: false,
        siteReadinessDate: moment(siteReadyByDate).format('DD/MM/YYYY'),
        siteInstallAppointmentDetail: {
          appointmentId: null
        }
      };
    }
  };

  const updateOrder = () => {
    const orderRequest = getUpdateOrderRequest();
    return updateOrderForSummary(orderRequest);
  };

  const onCancel = () => {
    setShowCancelPopup(true);
    // faOnDedicatedSummaryPageCancel();
    const lead = getCurrentLead();
    trackAnalytics({
      eVar9: `b2b|${order?.serviceType}|buy|confirm installations|site readiness|cancel`,
      prop0: `b2b|${order?.lead?.customer?.segment}|${order?.serviceType}`,
      prop15: order?.customerEmail || '',
      prop17: `${order?.lead?.customer?.authorizedSignatory?.firstName || ''} ${order?.lead?.customer?.authorizedSignatory?.lastName || ''}`, //full name
      prop14: order?.lead?.customer?.authorizedSignatory?.phoneNumber || '',
      prop35: order?.companyAddress?.officeName || '',
      prop39: order?.lead?.customer?.segment || '',
      prop40: order?.serviceType || '',
      prop37: order?.orderType || '',
      // prop41=<type of site>
      // Prop43=<no. of sites>
      prop21: order?.lead?.connectivityLineItems?.[0]?.feasibilityId || '',
      // Prop23=<link id>
      prop9: order?.companyAddress?.pincode || '',
      prop36: order?.companyAddress?.officeName || '',
      prop20: order?.companyAddress?.addressLine1 || '',
      prop26: planDetail?.speed || '',
      // Prop28=<site type>
      prop10: order?.orderId || '',
      prop29: order?.lead?.connectivityLineItems?.[0]?.selectedMedia?.[0]?.media || '',
      prop22: order?.lead?.connectivityLineItems?.[0]?.planDetail?.planPeriod || '',
      prop18: order?.lead?.planDetail?.totalPrice || '',
      prop1: order?.purchaseOrder?.poNumber || '',
      prop24: order?.documents?.PURCHASE_ORDER?.PO_DOCUMENT || '',
      prop25: order?.purchaseOrder?.purchaseOrderDate || '',
      prop2: order?.purchaseOrder?.poAmount || '',
      prop3: order?.billingType || '',
      prop4: order?.billingCycle || '',
      prop5: order?.lead?.customer?.panNumber || '',
      prop16: order?.companyAddress?.city || '',
      prop7: order?.siteIncharge?.firstName || '',
      prop8: order?.siteIncharge?.phoneNumber || '',
      prop9: order?.siteIncharge?.email || '',
      prop37: order?.lead?.connectivityLineItems?.[0]?.customerNeedDate || '',
      prop38: order?.fiscalCode?.id || ''
      // Prop11=<full name of CPM>
      // Prop12=<mobile number of CPM>
      // Prop13=<email address of CPM>
      // Prop14=<yes/no>
    });
  };

  const isOrderConfirmationPending = () => {
    return orderStatus === ORDER_TYPE.ORDER_CONFIRMATION_PENDING;
  };

  const onSubmit = () => {
    const params = {
      ...planDetail,
      billingType: billingLevel
    };
    trackAnalytics({
      eVar9: `b2b|${order.serviceType}|buy|confirm installations|site readiness|confirm order`,
      prop0: `b2b|${order?.lead?.customer?.segment}|${order.serviceType}`,
      prop15: order?.customerEmail || '',
      prop17: `${order?.lead?.customer?.authorizedSignatory?.firstName || ''} ${order?.lead?.customer?.authorizedSignatory?.lastName || ''}`, //full name
      prop14: order?.lead?.customer?.authorizedSignatory?.phoneNumber || '',
      prop35: order?.companyAddress?.officeName || '', //account selected
      prop39: order?.lead?.customer?.segment || '',
      prop40: order.serviceType || '',
      prop37: order?.orderType || '',
      // prop41=<type of site>
      // Prop43=<no. of sites>
      prop21: order?.lead?.connectivityLineItems?.[0]?.feasibilityId || '',
      // Prop23=<link id>
      prop9: order?.companyAddress?.pincode || '',
      prop36: order?.companyAddress?.officeName || '',
      prop20: order?.companyAddress?.addressLine1 || '',
      prop26: params?.speed || '',
      // Prop28=<site type>
      prop10: order?.orderId || '',
      prop29: order?.lead?.connectivityLineItems?.[0]?.selectedMedia?.[0]?.media || '',
      prop22: order?.lead?.connectivityLineItems?.[0]?.planDetail?.planPeriod || '',
      prop18: planDetail.price.amount * (planDetail.planPeriod / 12) + planDetail.price.otc || '',
      prop1: order?.purchaseOrder?.poNumber || '',
      prop24: order?.documents?.PURCHASE_ORDER?.PO_DOCUMENT || '',
      prop25: order?.purchaseOrder?.purchaseOrderDate || '',
      prop2: order?.purchaseOrder?.poAmount || '',
      prop3: order?.billingType || '',
      prop4: order?.billingCycle || '',
      prop5: order?.lead?.customer?.panNumber || '',
      prop16: order?.companyAddress?.city || '',
      prop7: order?.siteIncharge?.firstName || '',
      prop8: order?.siteIncharge?.phoneNumber || '',
      prop9: order?.siteIncharge?.email || '',
      prop37: order?.lead?.connectivityLineItems?.[0]?.customerNeedDate || '',
      prop38: order?.fiscalCode?.id || ''
      // Prop11=<full name of CPM>
      // Prop12=<mobile number of CPM>
      // Prop13=<email address of CPM>
      // Prop14=<yes/no>
    });
    // faOnDedicatedSummaryPageSubmit(params);
    if (selectedOption?.id === 'No' && siteReadyByDate && customerNeedByDate && siteReadyByDate > customerNeedByDate) {
      setError('Customer Need by Date cannot be less than Likely date of readiness');
      return false;
    }

    setError('');

    if (isOrderConfirmationPending()) {
      const cb = () => {
        setShowEmailPopup(true);
      };
      fetchOrder(cb);
    } else if (
      (selectedOption?.id === 'Yes' && appointmentData.appointmentDate && appointmentData.startTime && appointmentData.endTime && customerNeedByDate) ||
      (selectedOption?.id === 'Yes' && isThirdPartyMedia) ||
      (selectedOption?.id === 'No' && siteReadyByDate && customerNeedByDate) ||
      !isCircleEligible(singleSiteCircle)
    ) {
      updateOrder().then(
        (resp) => {
          setShowEmailPopup(true);
        },
        (error) => {
          const errorMsg = (error && error.response && error.response.data && error.response.data.errorMsg) || GENERIC_ERROR_MESSAGE;

          showServiceError({
            show: true,
            data: errorMsg,
            redirect: false
          });
        }
      );
    } else {
      setShowWidget(true);
    }
  };

  const onViewMoreLessClick = () => {
    setViewMore(!viewMore);
  };

  const setOTPVerification = (verified) => {
    if (verified) {
      submitOrder();
    }
  };

  const showNotificationForSentEmail = (orderStatus) => {
    if (orderStatus === ORDER_TYPE.ORDER_CONFIRMATION_PENDING) {
      const EmailSentmessage = (
        <p>
          Order confirmation link has been sent to <a href="javascript:void(0)"> {ocLinkSentAt}</a> to {NOTIFICATION_MESSAGE.CONFIRM_ORDER}
        </p>
      );
      const verifierMessage = <p>To verify the order click on {NOTIFICATION_MESSAGE.CONFIRM_ORDER}</p>;

      setNotification((prevState) => {
        return { ...prevState, show: true, message: isOrderConfirmation && isOrderConfirmationUrl() ? verifierMessage : EmailSentmessage };
      });
    }
  };

  const isSubmitDisabled = () => {
    if (isOrderConfirmationPending() && !isNull(customerNeedByDate)) {
      return false;
    }
    const isCPMDetailsFilled = cpmAccordionMeta.isValidated;

    return !isCPMDetailsFilled || (selectedOption?.id === 'No' && isNull(siteReadyByDate) && isNull(customerNeedByDate)) || isNull(customerNeedByDate);
  };

  useEffect(() => {
    showNotificationForSentEmail(orderStatus);
  }, [orderStatus]);

  useEffect(() => {
    document.getElementById('root').className = 'pricing-strip';
    return () => {
      document.getElementById('root').classList.remove('pricing-strip');
    };
  }, []);

  return (
    <React.Fragment>
      <div className={`selected-plans active`}>
        {/* add class "full_plans" to show pull plan items */}
        <div className="container">
          <div className={`inside-plan ${viewMore ? 'show_full_view' : ''}`}>
            <div className="plan-info">
              <div className="plan_details_veiw">
                <div className="left_col">
                  <h4>Total Amount</h4>
                  <div class="value mt-10">
                    &#8377; {planDetail ? numberWithCommas(planDetail.price.amount * (planDetail.planPeriod / 12) + planDetail.price.otc) : '-'}{' '}
                    <span className="gst_charges">+GST Taxes</span>
                  </div>
                </div>
                <div className="right_col">
                  <div className="view_type" onClick={onViewMoreLessClick}>
                    {viewMore ? 'View Less' : 'View More'} <span className={`animatedarrow ${viewMore ? 'arrow_up' : ''} `}></span>
                  </div>
                </div>
              </div>
            </div>
            <div className="plan-details">
              <div className="item_list">
                <div className="items">
                  <div className="label">SITES IN ORDER</div>
                  <div className="value">
                    <strong>{lineItemCount}</strong>{' '}
                  </div>
                </div>
              </div>
              <div className="action">
                <Button variant="outlinebtn" onClick={onCancel}>
                  Cancel
                </Button>
                <Button className="activebtn" disabled={showLoader || isSubmitDisabled()} onClick={onSubmit}>
                  Confirm Order
                  {showLoader && <span className="loading_icon"></span>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showCancelPopup && (
        <CancelOrderPopup
          onLoad={faOnDedicatedSummaryCancelOrderPopupLoad}
          onSubmit={(data) => faOnDedicatedSummaryCancelOrderPopupSubmit(data)}
          setshowPopup={setShowCancelPopup}
          isSummaryPage={true}
          metaData={order}
        ></CancelOrderPopup>
      )}

      {showEmailPopup && (
        <EmailPopup
          setNotification={setNotification}
          customer={customer}
          setshowPopup={setShowEmailPopup}
          email={ocLinkSentAt}
          setOTPVerification={setOTPVerification}
          orderStatus={orderStatus}
          fetchOrder={fetchOrder}
          isOrderConfirmation={isOrderConfirmation && isOrderConfirmationUrl()}
          order={order}
          planDetail={planDetail}
        ></EmailPopup>
      )}

      {showWidget && (
        <WidgetComponent
          open={showWidget}
          setOpen={setShowWidget}
          title="Schedule Appointment"
          appointmentData={appointmentData}
          setAppointmentData={setAppointmentData}
          siteReadyByDate={siteReadyByDate}
          setSiteReadyByDate={setSiteReadyByDate}
          selectedOption={selectedOption}
          appointmentId={appointmentId}
          setSelectedAppointmentId={setSelectedAppointmentId}
          fetchOrder={fetchOrder}
          customerNeedByDate={customerNeedByDate}
          setCustomerNeedByDate={setCustomerNeedByDate}
          planDetail={planDetail}
          orderDetail={order}
        />
      )}
    </React.Fragment>
  );
};

export default FooterComponent;
